import { useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';

import {
  compositionPageDimensionsMm,
  FILL_PAGE_BOUNDS,
  marginsMmToNormalizedBounds,
  normalizedBoundsToMarginsMm,
  type PageMarginsMm,
} from '../../composition/geometry';
import {
  COMPOSITION_NORTH_ARROW_STYLES,
  COMPOSITION_SCALE_BAR_THICKNESS_MAX_MM,
  COMPOSITION_SCALE_BAR_THICKNESS_MIN_MM,
  COMPOSITION_SOURCES_ATTRIBUTION_MAX_PT,
  COMPOSITION_SOURCES_ATTRIBUTION_MIN_PT,
  COMPOSITION_TYPOGRAPHY_MIN_PT,
  COMPOSITION_CUSTOM_PAGE_MAX_MM,
  COMPOSITION_CUSTOM_PAGE_MIN_MM,
  COMPOSITION_GRID_SPACING_MAX_MM,
  COMPOSITION_GRID_SPACING_MIN_MM,
  COMPOSITION_LEGEND_CORNER_RADIUS_MAX_MM,
  COMPOSITION_LEGEND_SPACING_MAX_MM,
  type CompositionPagePreset,
  type CompositionTypography,
} from '../../composition/model';
import { COMPOSITION_TEXT_MAX_LENGTH } from '../../composition/validation';
import { formatComposerNumber } from '../../composition/numericPresentation';
import {
  COMPOSITION_LOGO_ACCEPT,
  CompositionLogoImportError,
  compositionLogoBoundsForWidthMm,
  compositionLogoDisplayName,
  compositionLogoWidthRangeMm,
  importCompositionLogoFile,
} from '../../composition/logo';
import { COMPOSITION_NORTH_ARROW_STYLE_LABELS } from '../../composition/northArrow';
import {
  formatScaleDenominator,
  hasExactCompositionScale,
  parseScaleDenominator,
} from '../../composition/scale';
import { Icon } from '../../components/Icon';
import { useCapability } from '../../features/useCapability';
import { LABEL_FONT_FAMILIES } from '../../labels/fonts';
import { supportsAuthoredItalic } from '../../typography/fonts';
import { deriveCompositionLegend } from '../../composition/legend';
import type { CompositionInspectorSection } from '../state/appStore';
import { useAppStore } from '../state/useAppStore';
import { ColourControl } from './style/ColourControl';
import { ExportMapDialog } from './composition/ExportMapDialog';
import { staticBasemapAttributionText } from '../../basemaps/catalog';
import { createDefaultInsetMap } from '../../composition/inset';
import { NorthArrowSymbol } from './composition/NorthArrowSymbol';
import { TemplatePicker } from './composition/TemplatePicker';
import { LEGACY_SCALE_BAR_PANEL } from '../../composition/panelAppearance';

const SECTIONS: readonly { readonly id: CompositionInspectorSection; readonly label: string }[] = [
  { id: 'page', label: 'Page & Templates' },
  { id: 'map-frame', label: 'Map frame' },
  { id: 'title', label: 'Title' },
  { id: 'subtitle', label: 'Subtitle' },
  { id: 'legend', label: 'Legend' },
  { id: 'scale-bar', label: 'Scale bar' },
  { id: 'north-arrow', label: 'North arrow' },
  { id: 'logo', label: 'Logo' },
  { id: 'inset-map', label: 'Inset map' },
  { id: 'sources-attribution', label: 'Sources & attribution' },
  { id: 'guides', label: 'Guides' },
];

function ScaleDenominatorInput({
  value,
  disabled,
  onCommit,
}: {
  readonly value: number;
  readonly disabled: boolean;
  readonly onCommit: (value: number) => void;
}) {
  const authoredValue = String(value);
  const [draft, setDraft] = useState<{ readonly baseValue: string; readonly value: string } | null>(
    null,
  );
  const displayValue =
    draft?.baseValue === authoredValue ? draft.value : formatScaleDenominator(value);
  const commit = (text: string) => {
    const parsed = parseScaleDenominator(text);
    setDraft(null);
    if (parsed !== null) onCommit(parsed);
  };
  return (
    <div className="composition-scale-input">
      <span aria-hidden="true">1 :</span>
      <input
        aria-label="Scale denominator"
        disabled={disabled}
        id="composition-scale-denominator"
        inputMode="numeric"
        maxLength={16}
        onBlur={(event) => commit(event.currentTarget.value)}
        onChange={(event) => setDraft({ baseValue: authoredValue, value: event.target.value })}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
          if (event.key === 'Escape') {
            setDraft(null);
            event.currentTarget.blur();
          }
        }}
        type="text"
        value={displayValue}
      />
    </div>
  );
}

function AccordionSection({
  id,
  label,
  open,
  onOpen,
  children,
}: {
  readonly id: CompositionInspectorSection;
  readonly label: string;
  readonly open: boolean;
  readonly onOpen: () => void;
  readonly children: ReactNode;
}) {
  return (
    <section
      className={`composition-accordion-section${open ? ' composition-accordion-section-open' : ''}`}
    >
      <button
        aria-controls={`composition-section-${id}`}
        aria-expanded={open}
        className="composition-accordion-trigger"
        onClick={onOpen}
        type="button"
      >
        <span>{label}</span>
        <Icon name={open ? 'chevron-down' : 'chevron-right'} />
      </button>
      {open ? (
        <div className="composition-accordion-content" id={`composition-section-${id}`}>
          {children}
        </div>
      ) : null}
    </section>
  );
}

function NumericDraftInput({
  id,
  value,
  min,
  max,
  step,
  readOnly = false,
  onCommit,
  describedBy,
}: {
  readonly id: string;
  readonly value: number;
  readonly min: number;
  readonly max?: number;
  readonly step?: number;
  readonly readOnly?: boolean;
  readonly onCommit: (value: number) => void;
  readonly describedBy?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string | null>(null);
  const cancelledRef = useRef(false);
  const displayValue = editing ? (draft ?? String(value)) : formatComposerNumber(value);
  const commit = () => {
    setEditing(false);
    setDraft(null);
    if (cancelledRef.current || draft === null || draft.trim() === '' || readOnly) return;
    const parsed = Number(draft);
    if (!Number.isFinite(parsed)) {
      return;
    }
    const next = Math.min(max ?? Infinity, Math.max(min, parsed));
    if (next !== value) onCommit(next);
  };
  return (
    <input
      aria-describedby={describedBy}
      id={id}
      max={max}
      min={min}
      onBlur={commit}
      onChange={(event) => {
        const nextDraft = event.target.value;
        setDraft(nextDraft);
        const parsed = Number(nextDraft);
        if (
          nextDraft.trim() !== '' &&
          Number.isFinite(parsed) &&
          parsed >= min &&
          (max === undefined || parsed <= max) &&
          parsed !== value
        ) {
          onCommit(parsed);
        }
      }}
      onFocus={() => {
        cancelledRef.current = false;
        flushSync(() => {
          setDraft(null);
          setEditing(true);
        });
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.currentTarget.blur();
        if (event.key === 'Escape') {
          cancelledRef.current = true;
          setDraft(null);
          event.currentTarget.blur();
        }
      }}
      readOnly={readOnly}
      step={step}
      type="number"
      value={displayValue}
    />
  );
}

function TypographyControls({
  id,
  typography,
  minimumFontSizePt = COMPOSITION_TYPOGRAPHY_MIN_PT,
  maximumFontSizePt = 96,
  onChange,
}: {
  readonly id: string;
  readonly typography: CompositionTypography;
  readonly minimumFontSizePt?: number;
  readonly maximumFontSizePt?: number;
  readonly onChange: (typography: CompositionTypography) => void;
}) {
  const style = `${typography.fontWeight}-${typography.fontStyle}`;
  return (
    <div className="composition-typography-controls">
      <label className="composition-field" htmlFor={`${id}-font`}>
        <span>Font family</span>
        <select
          id={`${id}-font`}
          onChange={(event) =>
            onChange({
              ...typography,
              fontFamily: event.target.value as CompositionTypography['fontFamily'],
              ...(!supportsAuthoredItalic(event.target.value as CompositionTypography['fontFamily'])
                ? { fontStyle: 'normal' as const }
                : {}),
            })
          }
          value={typography.fontFamily}
        >
          {LABEL_FONT_FAMILIES.map((font) => (
            <option key={font.id} value={font.id}>
              {font.label}
            </option>
          ))}
        </select>
      </label>
      <div className="composition-typography-row">
        <label className="composition-field" htmlFor={`${id}-style`}>
          <span>Font style</span>
          <select
            id={`${id}-style`}
            onChange={(event) => {
              const [fontWeight, fontStyle] = event.target.value.split('-') as [
                CompositionTypography['fontWeight'],
                CompositionTypography['fontStyle'],
              ];
              onChange({ ...typography, fontWeight, fontStyle });
            }}
            value={style}
          >
            <option value="regular-normal">Regular</option>
            <option value="bold-normal">Bold</option>
            <option
              value="regular-italic"
              disabled={!supportsAuthoredItalic(typography.fontFamily)}
            >
              Italic
            </option>
            <option value="bold-italic" disabled={!supportsAuthoredItalic(typography.fontFamily)}>
              Bold Italic
            </option>
          </select>
        </label>
        <label className="composition-field composition-size-field" htmlFor={`${id}-size`}>
          <span>Size (pt)</span>
          <NumericDraftInput
            id={`${id}-size`}
            max={maximumFontSizePt}
            min={minimumFontSizePt}
            onCommit={(fontSizePt) => onChange({ ...typography, fontSizePt })}
            value={typography.fontSizePt}
          />
        </label>
      </div>
      <div className="composition-typography-row composition-typography-colour-row">
        <ColourControl
          id={`${id}-colour`}
          label="Colour"
          onChange={(colour) => onChange({ ...typography, colour })}
          value={typography.colour}
        />
        <fieldset className="composition-alignment-control">
          <legend>Alignment</legend>
          <div>
            {(['left', 'centre', 'right'] as const).map((alignment) => (
              <button
                aria-label={`Align ${alignment}`}
                aria-pressed={typography.alignment === alignment}
                className={typography.alignment === alignment ? 'selected' : undefined}
                key={alignment}
                onClick={() => onChange({ ...typography, alignment })}
                title={`Align ${alignment}`}
                type="button"
              >
                <Icon name={`align-${alignment}`} />
              </button>
            ))}
          </div>
        </fieldset>
      </div>
    </div>
  );
}

export function CompositionPanel() {
  const canUseCustom = useCapability('composition.customPageSize');
  const composition = useAppStore((state) => state.project.composition);
  const basemap = useAppStore((state) => state.project.map.basemap);
  const layers = useAppStore((state) => state.project.layers);
  const sources = useAppStore((state) => state.project.sources);
  const annotationLayers = useAppStore((state) => state.project.annotationLayers);
  const section = useAppStore((state) => state.shell.compositionInspectorSection);
  const gridVisible = useAppStore((state) => state.shell.compositionGridVisible);
  const gridSpacingMm = useAppStore((state) => state.shell.compositionGridSpacingMm);
  const objectSnapEnabled = useAppStore((state) => state.shell.compositionObjectSnapEnabled);
  const setObjectSnapEnabled = useAppStore((state) => state.setCompositionObjectSnapEnabled);
  const snapEnabled = useAppStore((state) => state.shell.compositionSnapEnabled);
  const updatePage = useAppStore((state) => state.updateCompositionPage);
  const applyTemplate = useAppStore((state) => state.applyCompositionTemplate);
  const updateRadius = useAppStore((state) => state.updateCompositionMapFrameRadius);
  const scalePanel = composition.scaleBar.panel ?? LEGACY_SCALE_BAR_PANEL;
  const updateBounds = useAppStore((state) => state.updateCompositionMapFrameBounds);
  const updateBorder = useAppStore((state) => state.updateCompositionMapFrameBorder);
  const updateCamera = useAppStore((state) => state.updateCompositionCamera);
  const useEditorView = useAppStore((state) => state.useEditorViewForComposition);
  const updateText = useAppStore((state) => state.updateCompositionText);
  const updateLegend = useAppStore((state) => state.updateCompositionLegend);
  const updateScaleBar = useAppStore((state) => state.updateCompositionScaleBar);
  const updateNorthArrow = useAppStore((state) => state.updateCompositionNorthArrow);
  const updateInset = useAppStore((state) => state.updateCompositionInsetMap);
  const updateLogo = useAppStore((state) => state.updateCompositionLogo);
  const selectCompositionElement = useAppStore((state) => state.selectCompositionElement);
  const updateSourcesAttribution = useAppStore(
    (state) => state.updateCompositionSourcesAttribution,
  );
  const setSection = useAppStore((state) => state.setCompositionInspectorSection);
  const setGridVisible = useAppStore((state) => state.setCompositionGridVisible);
  const setGridSpacingMm = useAppStore((state) => state.setCompositionGridSpacingMm);
  const setSnapEnabled = useAppStore((state) => state.setCompositionSnapEnabled);
  const dimensions = compositionPageDimensionsMm(composition.page);
  const margins = normalizedBoundsToMarginsMm(composition.mapFrame.bounds, dimensions);
  const customLocked = composition.page.preset === 'custom' && !canUseCustom;
  const legendGroups = deriveCompositionLegend(
    sources,
    layers.map((layer) => ({ ...layer, visible: true })),
    [],
    {},
    annotationLayers.map((layer) => ({
      ...layer,
      visible: true,
      annotations: layer.annotations.map((annotation) => ({ ...annotation, visible: true })),
    })),
    composition.legend.hiddenGroupHeadingIds,
  );
  const exactScale = hasExactCompositionScale(composition.mapFrame.camera.pitch);
  const mandatoryAttribution = staticBasemapAttributionText(basemap);
  const [exportOpen, setExportOpen] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoImporting, setLogoImporting] = useState(false);
  const exportButtonRef = useRef<HTMLButtonElement | null>(null);
  const logoInputRef = useRef<HTMLInputElement | null>(null);
  const logoActionRef = useRef<HTMLButtonElement | null>(null);
  const insetActionRef = useRef<HTMLButtonElement | null>(null);
  const closeExport = () => {
    setExportOpen(false);
    requestAnimationFrame(() => exportButtonRef.current?.focus());
  };

  const changeMargin = (side: keyof PageMarginsMm, value: number) => {
    const next = marginsMmToNormalizedBounds({ ...margins, [side]: value }, dimensions, 0.1);
    if (next) updateBounds(next);
  };
  const open = (id: CompositionInspectorSection) => section === id;
  const importLogo = async (file: File) => {
    setLogoError(null);
    setLogoImporting(true);
    try {
      const logo = await importCompositionLogoFile(file, dimensions);
      const result = updateLogo(logo);
      if (!result.ok) {
        setLogoError(result.message);
        return;
      }
      selectCompositionElement('logo');
    } catch (error) {
      setLogoError(
        error instanceof CompositionLogoImportError
          ? error.message
          : 'The selected image could not be added.',
      );
    } finally {
      setLogoImporting(false);
    }
  };

  return (
    <aside className="composition-panel" aria-label="Composition controls">
      <div className="composition-panel-heading">
        <h2>Layout</h2>
        <button
          className="composition-export-action"
          onClick={() => setExportOpen(true)}
          ref={exportButtonRef}
          type="button"
        >
          Export map
        </button>
      </div>
      <div className="composition-controls">
        {SECTIONS.map(({ id, label }) => (
          <AccordionSection
            id={id}
            key={id}
            label={label}
            onOpen={() => setSection(section === id ? null : id)}
            open={open(id)}
          >
            {id === 'page' ? (
              <>
                <TemplatePicker onApply={applyTemplate} />
                <label className="composition-field" htmlFor="composition-page-size">
                  <span>Size</span>
                  <select
                    disabled={customLocked}
                    id="composition-page-size"
                    onChange={(event) => {
                      const preset = event.target.value as CompositionPagePreset;
                      if (preset === 'custom')
                        updatePage({
                          preset,
                          orientation: 'portrait',
                          customWidthMm: dimensions.width,
                          customHeightMm: dimensions.height,
                        });
                      else updatePage({ preset, customWidthMm: null, customHeightMm: null });
                    }}
                    value={composition.page.preset}
                  >
                    <option value="a5">A5</option>
                    <option value="a4">A4</option>
                    <option value="a3">A3</option>
                    <option value="letter">US Letter</option>
                    {canUseCustom || composition.page.preset === 'custom' ? (
                      <option value="custom">Custom</option>
                    ) : null}
                  </select>
                </label>
                {composition.page.preset === 'custom' ? (
                  <>
                    <div className="composition-dimension-grid">
                      <label className="composition-field" htmlFor="composition-custom-width">
                        <span>Width (mm)</span>
                        <NumericDraftInput
                          describedBy="composition-custom-size-limits"
                          id="composition-custom-width"
                          max={COMPOSITION_CUSTOM_PAGE_MAX_MM}
                          min={COMPOSITION_CUSTOM_PAGE_MIN_MM}
                          onCommit={(customWidthMm) => updatePage({ customWidthMm })}
                          readOnly={customLocked}
                          value={composition.page.customWidthMm ?? dimensions.width}
                        />
                      </label>
                      <label className="composition-field" htmlFor="composition-custom-height">
                        <span>Height (mm)</span>
                        <NumericDraftInput
                          describedBy="composition-custom-size-limits"
                          id="composition-custom-height"
                          max={COMPOSITION_CUSTOM_PAGE_MAX_MM}
                          min={COMPOSITION_CUSTOM_PAGE_MIN_MM}
                          onCommit={(customHeightMm) => updatePage({ customHeightMm })}
                          readOnly={customLocked}
                          value={composition.page.customHeightMm ?? dimensions.height}
                        />
                      </label>
                    </div>
                    <small
                      className="composition-clipping-note"
                      id="composition-custom-size-limits"
                    >
                      Range: {COMPOSITION_CUSTOM_PAGE_MIN_MM}–{COMPOSITION_CUSTOM_PAGE_MAX_MM} mm
                      each
                    </small>
                  </>
                ) : (
                  <label className="composition-field" htmlFor="composition-orientation">
                    <span>Orientation</span>
                    <select
                      id="composition-orientation"
                      onChange={(event) =>
                        updatePage({
                          orientation: event.target.value as 'landscape' | 'portrait',
                        })
                      }
                      value={composition.page.orientation}
                    >
                      <option value="landscape">Landscape</option>
                      <option value="portrait">Portrait</option>
                    </select>
                  </label>
                )}
                {composition.page.preset !== 'custom' ? (
                  <small className="composition-page-dimensions">
                    {dimensions.width} × {dimensions.height} mm
                  </small>
                ) : customLocked ? (
                  <small className="composition-page-dimensions">
                    Custom dimensions are read-only in Free
                  </small>
                ) : null}
                <label className="composition-check">
                  <input
                    checked={composition.page.scaleLayoutWithPage}
                    onChange={(event) => updatePage({ scaleLayoutWithPage: event.target.checked })}
                    type="checkbox"
                  />
                  <span>Scale layout with page size</span>
                </label>
                <ColourControl
                  id="composition-page-colour"
                  label="Background"
                  onChange={(backgroundColour) => updatePage({ backgroundColour })}
                  value={composition.page.backgroundColour}
                />
              </>
            ) : null}

            {id === 'map-frame' ? (
              <>
                <label className="composition-field" htmlFor="composition-scale-denominator">
                  <span>Scale</span>
                  <ScaleDenominatorInput
                    disabled={!exactScale}
                    onCommit={(scaleDenominator) =>
                      updateCamera({ ...composition.mapFrame.camera, scaleDenominator })
                    }
                    value={composition.mapFrame.camera.scaleDenominator}
                  />
                </label>
                {!exactScale ? (
                  <div className="composition-scale-note">
                    <small>Exact physical scale is available when pitch is 0°.</small>
                    <button
                      className="composition-secondary-action"
                      onClick={() => updateCamera({ ...composition.mapFrame.camera, pitch: 0 })}
                      type="button"
                    >
                      Reset pitch
                    </button>
                  </div>
                ) : (
                  <small className="composition-page-dimensions">
                    Local representative fraction at the map centre.
                  </small>
                )}
                <div className="composition-margin-grid">
                  {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
                    <label
                      className="composition-field"
                      htmlFor={`composition-margin-${side}`}
                      key={side}
                    >
                      <span>{side[0]!.toUpperCase() + side.slice(1)} (mm)</span>
                      <NumericDraftInput
                        id={`composition-margin-${side}`}
                        min={0}
                        onCommit={(value) => changeMargin(side, value)}
                        step={0.1}
                        value={margins[side]}
                      />
                    </label>
                  ))}
                </div>
                <button
                  aria-label="Fill map frame to page"
                  className="composition-secondary-action composition-icon-action"
                  onClick={() => updateBounds(FILL_PAGE_BOUNDS)}
                  title="Fill map frame to page"
                  type="button"
                >
                  <Icon name="fit" />
                  Fill page
                </button>
                <label className="composition-check">
                  <input
                    checked={composition.mapFrame.border.enabled}
                    onChange={(event) => updateBorder({ enabled: event.target.checked })}
                    type="checkbox"
                  />
                  <span>Show border</span>
                </label>
                <label className="composition-field" htmlFor="composition-map-radius">
                  <span>Corner radius (mm)</span>
                  <NumericDraftInput
                    id="composition-map-radius"
                    min={0}
                    max={50}
                    step={0.1}
                    value={composition.mapFrame.cornerRadiusMm ?? 0}
                    onCommit={updateRadius}
                  />
                </label>
                <label className="composition-field" htmlFor="composition-border-width">
                  <span>Border width (mm)</span>
                  <NumericDraftInput
                    id="composition-border-width"
                    max={5}
                    min={0.1}
                    onCommit={(widthMm) => updateBorder({ widthMm })}
                    step={0.1}
                    value={composition.mapFrame.border.widthMm}
                  />
                </label>
                <ColourControl
                  id="composition-border-colour"
                  label="Border colour"
                  onChange={(colour) => updateBorder({ colour })}
                  value={composition.mapFrame.border.colour}
                />
                <button
                  className="composition-secondary-action"
                  onClick={useEditorView}
                  type="button"
                >
                  Use editor view
                </button>
              </>
            ) : null}

            {id === 'title' || id === 'subtitle'
              ? (() => {
                  const element = composition[id];
                  const capital = id === 'title' ? 'Title' : 'Subtitle';
                  return (
                    <>
                      <label className="composition-check">
                        <input
                          checked={element.enabled}
                          onChange={(event) => updateText(id, { enabled: event.target.checked })}
                          type="checkbox"
                        />
                        <span>Show {id}</span>
                      </label>
                      <label className="composition-field" htmlFor={`composition-${id}-text`}>
                        <span>Text</span>
                        <input
                          id={`composition-${id}-text`}
                          maxLength={COMPOSITION_TEXT_MAX_LENGTH}
                          onChange={(event) => updateText(id, { text: event.target.value })}
                          type="text"
                          value={element.text}
                        />
                      </label>
                      <TypographyControls
                        id={`composition-${id}`}
                        onChange={(typography) => updateText(id, { typography })}
                        typography={element.typography}
                      />
                      <span className="visually-hidden">{capital} typography</span>
                    </>
                  );
                })()
              : null}

            {id === 'legend' ? (
              <>
                <label className="composition-check">
                  <input
                    checked={composition.legend.enabled}
                    onChange={(event) => updateLegend({ enabled: event.target.checked })}
                    type="checkbox"
                  />
                  <span>Show legend</span>
                </label>
                <label className="composition-check">
                  <input
                    checked={composition.legend.background.enabled}
                    onChange={(event) =>
                      updateLegend({
                        background: {
                          ...composition.legend.background,
                          enabled: event.target.checked,
                        },
                      })
                    }
                    type="checkbox"
                  />
                  <span>Legend background</span>
                </label>
                <ColourControl
                  id="composition-legend-background"
                  label="Background colour"
                  onChange={(colour) =>
                    updateLegend({ background: { ...composition.legend.background, colour } })
                  }
                  value={composition.legend.background.colour}
                />
                <label className="composition-field" htmlFor="composition-legend-opacity">
                  <span>Background opacity (%)</span>
                  <input
                    id="composition-legend-opacity"
                    max="100"
                    min="0"
                    onChange={(event) => {
                      if (Number.isFinite(event.target.valueAsNumber))
                        updateLegend({
                          background: {
                            ...composition.legend.background,
                            opacity: event.target.valueAsNumber,
                          },
                        });
                    }}
                    type="number"
                    value={composition.legend.background.opacity}
                  />
                </label>
                <label className="composition-check">
                  <input
                    checked={composition.legend.border.enabled}
                    onChange={(event) =>
                      updateLegend({
                        border: { ...composition.legend.border, enabled: event.target.checked },
                      })
                    }
                    type="checkbox"
                  />
                  <span>Show border</span>
                </label>
                <div className="composition-legend-border-row">
                  <label className="composition-field" htmlFor="composition-legend-border-width">
                    <span>Border width (mm)</span>
                    <NumericDraftInput
                      id="composition-legend-border-width"
                      max={5}
                      min={0.1}
                      onCommit={(widthMm) =>
                        updateLegend({ border: { ...composition.legend.border, widthMm } })
                      }
                      step={0.1}
                      value={composition.legend.border.widthMm}
                    />
                  </label>
                  <ColourControl
                    id="composition-legend-border-colour"
                    label="Border colour"
                    onChange={(colour) =>
                      updateLegend({ border: { ...composition.legend.border, colour } })
                    }
                    value={composition.legend.border.colour}
                  />
                </div>
                <label className="composition-field" htmlFor="composition-legend-corner-radius">
                  <span>Corner radius (mm)</span>
                  <NumericDraftInput
                    id="composition-legend-corner-radius"
                    max={COMPOSITION_LEGEND_CORNER_RADIUS_MAX_MM}
                    min={0}
                    onCommit={(cornerRadiusMm) => updateLegend({ cornerRadiusMm })}
                    step={0.5}
                    value={composition.legend.cornerRadiusMm}
                  />
                </label>
                <h4>Body typography</h4>
                <TypographyControls
                  id="composition-legend-body"
                  onChange={(bodyTypography) => updateLegend({ bodyTypography })}
                  typography={composition.legend.bodyTypography}
                />
                <label className="composition-check">
                  <input
                    checked={composition.legend.heading.enabled}
                    onChange={(event) =>
                      updateLegend({
                        heading: { ...composition.legend.heading, enabled: event.target.checked },
                      })
                    }
                    type="checkbox"
                  />
                  <span>Show legend heading</span>
                </label>
                {composition.legend.heading.enabled ? (
                  <>
                    <label className="composition-field" htmlFor="composition-legend-heading-text">
                      <span>Heading text</span>
                      <input
                        id="composition-legend-heading-text"
                        maxLength={COMPOSITION_TEXT_MAX_LENGTH}
                        onChange={(event) =>
                          updateLegend({
                            heading: { ...composition.legend.heading, text: event.target.value },
                          })
                        }
                        type="text"
                        value={composition.legend.heading.text}
                      />
                    </label>
                    <h4>Heading typography</h4>
                    <TypographyControls
                      id="composition-legend-heading"
                      onChange={(typography) =>
                        updateLegend({ heading: { ...composition.legend.heading, typography } })
                      }
                      typography={composition.legend.heading.typography}
                    />
                  </>
                ) : null}
                <h4>Spacing</h4>
                <div className="composition-legend-spacing-grid">
                  {(
                    [
                      ['Top', 'paddingTopMm'],
                      ['Right', 'paddingRightMm'],
                      ['Bottom', 'paddingBottomMm'],
                      ['Left', 'paddingLeftMm'],
                    ] as const
                  ).map(([label, key]) => (
                    <label
                      className="composition-field"
                      htmlFor={`composition-legend-${key}`}
                      key={key}
                    >
                      <span>{label} (mm)</span>
                      <NumericDraftInput
                        id={`composition-legend-${key}`}
                        max={COMPOSITION_LEGEND_SPACING_MAX_MM}
                        min={0}
                        onCommit={(value) => updateLegend({ [key]: value })}
                        step={0.5}
                        value={composition.legend[key]}
                      />
                    </label>
                  ))}
                  <label className="composition-field" htmlFor="composition-legend-group-spacing">
                    <span>Group spacing (mm)</span>
                    <NumericDraftInput
                      id="composition-legend-group-spacing"
                      max={COMPOSITION_LEGEND_SPACING_MAX_MM}
                      min={0}
                      onCommit={(groupSpacingMm) => updateLegend({ groupSpacingMm })}
                      step={0.5}
                      value={composition.legend.groupSpacingMm}
                    />
                  </label>
                  <label
                    className="composition-field composition-symbol-size-control"
                    htmlFor="composition-legend-item-spacing"
                  >
                    <span>Item spacing (mm)</span>
                    <NumericDraftInput
                      id="composition-legend-item-spacing"
                      max={COMPOSITION_LEGEND_SPACING_MAX_MM}
                      min={0}
                      onCommit={(itemSpacingMm) => updateLegend({ itemSpacingMm })}
                      step={0.5}
                      value={composition.legend.itemSpacingMm}
                    />
                  </label>
                </div>
                <label
                  className="composition-field composition-symbol-size-control"
                  htmlFor="composition-legend-symbol-size"
                >
                  <span>Symbol size (%)</span>
                  <NumericDraftInput
                    id="composition-legend-symbol-size"
                    min={75}
                    max={150}
                    step={5}
                    value={composition.legend.symbolSizePercent ?? 100}
                    onCommit={(symbolSizePercent) => updateLegend({ symbolSizePercent })}
                  />
                </label>
                <div className="composition-column-settings">
                  <label
                    className="composition-field composition-columns-control"
                    htmlFor="composition-legend-columns"
                  >
                    <span>Columns</span>
                    <select
                      id="composition-legend-columns"
                      value={composition.legend.columnCount ?? 1}
                      onChange={(event) =>
                        updateLegend({ columnCount: Number(event.target.value) as 1 | 2 | 3 })
                      }
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                    </select>
                  </label>
                  {(composition.legend.columnCount ?? 1) > 1 ? (
                    <label className="composition-check">
                      <input
                        type="checkbox"
                        checked={composition.legend.repeatContinuationHeadings ?? true}
                        onChange={(event) =>
                          updateLegend({ repeatContinuationHeadings: event.target.checked })
                        }
                      />
                      <span>Repeat continued headings</span>
                    </label>
                  ) : null}
                </div>
                <fieldset className="composition-legend-layers">
                  <legend>Legend items</legend>
                  {legendGroups.map((group) => {
                    const included = !composition.legend.excludedLayerIds.includes(group.layerId);
                    const headingShown = !composition.legend.hiddenGroupHeadingIds.includes(
                      group.layerId,
                    );
                    const excludedEntries =
                      composition.legend.excludedEntryKeysByLayer[group.layerId] ?? [];
                    return (
                      <div className="composition-legend-layer" key={group.layerId}>
                        <label className="composition-check">
                          <input
                            aria-label={`Include ${group.name} in legend`}
                            checked={included}
                            onChange={(event) =>
                              updateLegend({
                                excludedLayerIds: event.target.checked
                                  ? composition.legend.excludedLayerIds.filter(
                                      (id) => id !== group.layerId,
                                    )
                                  : [...composition.legend.excludedLayerIds, group.layerId],
                              })
                            }
                            type="checkbox"
                          />
                          <span>{group.name}</span>
                        </label>
                        <div className="composition-legend-entries">
                          <label className="composition-check">
                            <input
                              aria-label={`Show heading for ${group.name}`}
                              checked={headingShown}
                              disabled={!included}
                              onChange={(event) =>
                                updateLegend({
                                  hiddenGroupHeadingIds: event.target.checked
                                    ? composition.legend.hiddenGroupHeadingIds.filter(
                                        (id) => id !== group.layerId,
                                      )
                                    : [...composition.legend.hiddenGroupHeadingIds, group.layerId],
                                })
                              }
                              type="checkbox"
                            />
                            <span>Show heading</span>
                          </label>
                          {group.entries.map((entry) => (
                            <label className="composition-check" key={entry.id}>
                              <input
                                aria-label={`Include ${entry.label} in ${group.name} legend`}
                                checked={!excludedEntries.includes(entry.id)}
                                disabled={!included}
                                onChange={(event) => {
                                  const next = event.target.checked
                                    ? excludedEntries.filter((key) => key !== entry.id)
                                    : [...excludedEntries, entry.id];
                                  const { [group.layerId]: _current, ...otherLayers } =
                                    composition.legend.excludedEntryKeysByLayer;
                                  void _current;
                                  updateLegend({
                                    excludedEntryKeysByLayer: next.length
                                      ? { ...otherLayers, [group.layerId]: next }
                                      : otherLayers,
                                  });
                                }}
                                type="checkbox"
                              />
                              <span>{entry.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </fieldset>
                <button
                  className="composition-secondary-action composition-icon-action"
                  onClick={() => updateLegend({ autoFit: true })}
                  type="button"
                >
                  <Icon name="fit" /> Fit to content
                </button>
                <small className="composition-clipping-note">
                  Legend content is clipped to its authored box.
                </small>
              </>
            ) : null}

            {id === 'scale-bar' ? (
              <>
                <label className="composition-check">
                  <input
                    checked={composition.scaleBar.enabled}
                    disabled={!exactScale}
                    onChange={(event) => updateScaleBar({ enabled: event.target.checked })}
                    type="checkbox"
                  />
                  <span>Show scale bar</span>
                </label>
                {!exactScale ? (
                  <small className="composition-clipping-note">
                    Reset map pitch to 0° before showing an exact scale bar.
                  </small>
                ) : null}
                <label className="composition-field" htmlFor="composition-scale-bar-units">
                  <span>Units</span>
                  <select
                    id="composition-scale-bar-units"
                    onChange={(event) =>
                      updateScaleBar({ units: event.target.value as 'metric' | 'imperial' })
                    }
                    value={composition.scaleBar.units}
                  >
                    <option value="metric">Metric</option>
                    <option value="imperial">Imperial</option>
                  </select>
                </label>
                <label className="composition-field" htmlFor="composition-scale-bar-width">
                  <span>Maximum width (mm)</span>
                  <NumericDraftInput
                    id="composition-scale-bar-width"
                    max={100}
                    min={10}
                    onCommit={(maximumWidthMm) => updateScaleBar({ maximumWidthMm })}
                    step={1}
                    value={composition.scaleBar.maximumWidthMm}
                  />
                </label>
                <label className="composition-field" htmlFor="composition-scale-bar-font-size">
                  <span>Font size (pt)</span>
                  <NumericDraftInput
                    id="composition-scale-bar-font-size"
                    max={24}
                    min={COMPOSITION_TYPOGRAPHY_MIN_PT}
                    onCommit={(fontSizePt) => updateScaleBar({ fontSizePt })}
                    step={1}
                    value={composition.scaleBar.fontSizePt}
                  />
                </label>
                <label className="composition-field" htmlFor="composition-scale-bar-thickness">
                  <span>Bar thickness (mm)</span>
                  <NumericDraftInput
                    id="composition-scale-bar-thickness"
                    max={COMPOSITION_SCALE_BAR_THICKNESS_MAX_MM}
                    min={COMPOSITION_SCALE_BAR_THICKNESS_MIN_MM}
                    onCommit={(thicknessMm) => updateScaleBar({ thicknessMm })}
                    step={0.05}
                    value={composition.scaleBar.thicknessMm}
                  />
                </label>
                <ColourControl
                  id="composition-scale-bar-colour"
                  label="Colour"
                  onChange={(colour) => updateScaleBar({ colour })}
                  value={composition.scaleBar.colour}
                />
                <label className="composition-field" htmlFor="composition-scale-style">
                  <span>Style</span>
                  <select
                    id="composition-scale-style"
                    value={composition.scaleBar.style ?? 'line'}
                    onChange={(event) =>
                      updateScaleBar({
                        style: event.target.value as 'line' | 'segmented' | 'simple-segmented',
                      })
                    }
                  >
                    <option value="line">Line</option>
                    <option value="segmented">Segmented</option>
                    <option value="simple-segmented">Simple Segmented</option>
                  </select>
                </label>
                {composition.scaleBar.style === 'simple-segmented' ? (
                  <label className="composition-field" htmlFor="composition-scale-label-position">
                    <span>Labels</span>
                    <select
                      id="composition-scale-label-position"
                      value={composition.scaleBar.labelPosition ?? 'above'}
                      onChange={(event) =>
                        updateScaleBar({ labelPosition: event.target.value as 'above' | 'below' })
                      }
                    >
                      <option value="above">Above</option>
                      <option value="below">Below</option>
                    </select>
                  </label>
                ) : null}
                <label className="composition-check">
                  <input
                    type="checkbox"
                    checked={scalePanel.background.enabled}
                    onChange={(event) =>
                      updateScaleBar({
                        panel: {
                          ...scalePanel,
                          background: { ...scalePanel.background, enabled: event.target.checked },
                        },
                      })
                    }
                  />
                  <span>Show backdrop</span>
                </label>
                <ColourControl
                  id="composition-scale-background"
                  label="Backdrop colour"
                  value={scalePanel.background.colour}
                  onChange={(colour) =>
                    updateScaleBar({
                      panel: { ...scalePanel, background: { ...scalePanel.background, colour } },
                    })
                  }
                />
                {(
                  [
                    ['opacity', 'Backdrop opacity (%)', scalePanel.background.opacity, 0, 100],
                    ['radius', 'Backdrop corner radius (mm)', scalePanel.cornerRadiusMm, 0, 50],
                    ['padding', 'Backdrop padding (mm)', scalePanel.paddingMm, 0, 50],
                    ['border', 'Backdrop border width (mm)', scalePanel.border.widthMm, 0.1, 5],
                  ] as const
                ).map(([key, label, value, min, max]) => (
                  <label
                    className="composition-field"
                    key={key}
                    htmlFor={`composition-scale-panel-${key}`}
                  >
                    <span>{label}</span>
                    <NumericDraftInput
                      id={`composition-scale-panel-${key}`}
                      value={value}
                      min={min}
                      max={max}
                      step={0.1}
                      onCommit={(number) =>
                        updateScaleBar({
                          panel: {
                            ...scalePanel,
                            ...(key === 'opacity'
                              ? { background: { ...scalePanel.background, opacity: number } }
                              : key === 'radius'
                                ? { cornerRadiusMm: number }
                                : key === 'padding'
                                  ? { paddingMm: number }
                                  : { border: { ...scalePanel.border, widthMm: number } }),
                          },
                        })
                      }
                    />
                  </label>
                ))}
                <label className="composition-check">
                  <input
                    type="checkbox"
                    checked={scalePanel.border.enabled}
                    onChange={(event) =>
                      updateScaleBar({
                        panel: {
                          ...scalePanel,
                          border: { ...scalePanel.border, enabled: event.target.checked },
                        },
                      })
                    }
                  />
                  <span>Show backdrop border</span>
                </label>
                <ColourControl
                  id="composition-scale-panel-border-colour"
                  label="Backdrop border colour"
                  value={scalePanel.border.colour}
                  onChange={(colour) =>
                    updateScaleBar({
                      panel: { ...scalePanel, border: { ...scalePanel.border, colour } },
                    })
                  }
                />
              </>
            ) : null}

            {id === 'north-arrow' ? (
              <>
                <label className="composition-check">
                  <input
                    checked={composition.northArrow.enabled}
                    onChange={(event) => updateNorthArrow({ enabled: event.target.checked })}
                    type="checkbox"
                  />
                  <span>Show north arrow</span>
                </label>
                <label className="composition-field" htmlFor="composition-north-arrow-size">
                  <span>Size (mm)</span>
                  <NumericDraftInput
                    id="composition-north-arrow-size"
                    max={50}
                    min={5}
                    onCommit={(sizeMm) => updateNorthArrow({ sizeMm })}
                    step={1}
                    value={composition.northArrow.sizeMm}
                  />
                </label>
                <fieldset className="composition-north-arrow-style-field">
                  <legend>Style</legend>
                  <div
                    aria-label="North arrow style"
                    className="composition-north-arrow-style-options"
                    id="composition-north-arrow-style"
                    role="radiogroup"
                  >
                    {COMPOSITION_NORTH_ARROW_STYLES.map((style) => (
                      <button
                        aria-checked={composition.northArrow.style === style}
                        className="composition-north-arrow-style-option"
                        key={style}
                        onClick={() => updateNorthArrow({ style })}
                        role="radio"
                        type="button"
                      >
                        <NorthArrowSymbol
                          className="composition-north-arrow-style-preview"
                          showLabel={false}
                          symbolStyle={style}
                        />
                        <span>{COMPOSITION_NORTH_ARROW_STYLE_LABELS[style]}</span>
                      </button>
                    ))}
                  </div>
                </fieldset>
                <ColourControl
                  id="composition-north-arrow-colour"
                  label="Colour"
                  onChange={(colour) => updateNorthArrow({ colour })}
                  value={composition.northArrow.colour}
                />
                <label className="composition-check">
                  <input
                    checked={composition.northArrow.showLabel}
                    onChange={(event) => updateNorthArrow({ showLabel: event.target.checked })}
                    type="checkbox"
                  />
                  <span>Show N label</span>
                </label>
              </>
            ) : null}

            {id === 'inset-map' ? (
              composition.insetMap ? (
                <>
                  <label className="composition-check">
                    <input
                      type="checkbox"
                      checked={composition.insetMap.enabled !== false}
                      onChange={(event) =>
                        updateInset({ ...composition.insetMap!, enabled: event.target.checked })
                      }
                    />
                    <span>Show inset map</span>
                  </label>
                  <fieldset className="composition-legend-layers">
                    <legend>Inset content</legend>
                    {layers.map((layer) => (
                      <label className="composition-check" key={layer.id}>
                        <input
                          type="checkbox"
                          aria-label={`Show ${layer.name} in inset`}
                          checked={!composition.insetMap!.hiddenLayerIds.includes(layer.id)}
                          onChange={(event) =>
                            updateInset({
                              ...composition.insetMap!,
                              hiddenLayerIds: event.target.checked
                                ? composition.insetMap!.hiddenLayerIds.filter(
                                    (id) => id !== layer.id,
                                  )
                                : [...composition.insetMap!.hiddenLayerIds, layer.id],
                            })
                          }
                        />
                        <span>{layer.name}</span>
                      </label>
                    ))}
                    {annotationLayers.map((layer) => {
                      const enabled = !composition.insetMap!.hiddenAnnotationLayerIds.includes(
                        layer.id,
                      );
                      return (
                        <div className="composition-legend-layer" key={layer.id}>
                          <label className="composition-check">
                            <input
                              type="checkbox"
                              aria-label={`Show ${layer.name} in inset`}
                              checked={enabled}
                              onChange={(event) =>
                                updateInset({
                                  ...composition.insetMap!,
                                  hiddenAnnotationLayerIds: event.target.checked
                                    ? composition.insetMap!.hiddenAnnotationLayerIds.filter(
                                        (id) => id !== layer.id,
                                      )
                                    : [...composition.insetMap!.hiddenAnnotationLayerIds, layer.id],
                                })
                              }
                            />
                            <span>{layer.name}</span>
                          </label>
                          <div className="composition-legend-entries">
                            {layer.annotations.map((annotation) => (
                              <label className="composition-check" key={annotation.id}>
                                <input
                                  type="checkbox"
                                  aria-label={`Show ${annotation.name} in inset`}
                                  disabled={!enabled}
                                  checked={
                                    !composition.insetMap!.hiddenAnnotationIds.includes(
                                      annotation.id,
                                    )
                                  }
                                  onChange={(event) =>
                                    updateInset({
                                      ...composition.insetMap!,
                                      hiddenAnnotationIds: event.target.checked
                                        ? composition.insetMap!.hiddenAnnotationIds.filter(
                                            (id) => id !== annotation.id,
                                          )
                                        : [
                                            ...composition.insetMap!.hiddenAnnotationIds,
                                            annotation.id,
                                          ],
                                    })
                                  }
                                />
                                <span>{annotation.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </fieldset>
                  <div className="composition-dimension-grid">
                    {(['x', 'y', 'width', 'height'] as const).map((key) => {
                      const inset = composition.insetMap!;
                      const size =
                        key === 'x' || key === 'width' ? dimensions.width : dimensions.height;
                      return (
                        <label className="composition-field" key={key} htmlFor={`inset-${key}`}>
                          <span>{key.charAt(0).toUpperCase() + key.slice(1)} (mm)</span>
                          <NumericDraftInput
                            id={`inset-${key}`}
                            value={inset.bounds[key] * size}
                            min={key === 'width' || key === 'height' ? size * 0.05 : 0}
                            max={
                              size *
                              (key === 'x'
                                ? 1 - inset.bounds.width
                                : key === 'y'
                                  ? 1 - inset.bounds.height
                                  : key === 'width'
                                    ? 1 - inset.bounds.x
                                    : 1 - inset.bounds.y)
                            }
                            onCommit={(value) =>
                              updateInset({
                                ...inset,
                                bounds: { ...inset.bounds, [key]: value / size },
                              })
                            }
                          />
                        </label>
                      );
                    })}
                  </div>
                  <label className="composition-field" htmlFor="inset-context">
                    <span>Context margin (%)</span>
                    <NumericDraftInput
                      id="inset-context"
                      min={0}
                      max={500}
                      value={composition.insetMap.contextMargin}
                      onCommit={(contextMargin) =>
                        updateInset({ ...composition.insetMap!, contextMargin })
                      }
                    />
                  </label>
                  <label className="composition-check">
                    <input
                      type="checkbox"
                      checked={composition.insetMap.border.enabled}
                      onChange={(event) =>
                        updateInset({
                          ...composition.insetMap!,
                          border: {
                            ...composition.insetMap!.border,
                            enabled: event.target.checked,
                          },
                        })
                      }
                    />
                    Show border
                  </label>
                  {(
                    [
                      [
                        'border-width',
                        'Border width (mm)',
                        composition.insetMap.border.widthMm,
                        0.1,
                        5,
                      ],
                      ['radius', 'Corner radius (mm)', composition.insetMap.cornerRadiusMm, 0, 50],
                      [
                        'outline-width',
                        'Outline width (mm)',
                        composition.insetMap.indicator.outlineWidthMm,
                        0.1,
                        5,
                      ],
                      [
                        'fill-opacity',
                        'Fill opacity (%)',
                        composition.insetMap.indicator.fillOpacity,
                        0,
                        100,
                      ],
                    ] as const
                  ).map(([key, label, value, min, max]) => (
                    <label className="composition-field" key={key} htmlFor={`inset-${key}`}>
                      <span>{label}</span>
                      <NumericDraftInput
                        id={`inset-${key}`}
                        min={min}
                        max={max}
                        value={value}
                        onCommit={(number) => {
                          const inset = composition.insetMap!;
                          updateInset(
                            key === 'border-width'
                              ? { ...inset, border: { ...inset.border, widthMm: number } }
                              : key === 'radius'
                                ? { ...inset, cornerRadiusMm: number }
                                : {
                                    ...inset,
                                    indicator: {
                                      ...inset.indicator,
                                      [key === 'outline-width' ? 'outlineWidthMm' : 'fillOpacity']:
                                        number,
                                    },
                                  },
                          );
                        }}
                      />
                    </label>
                  ))}
                  {(['border', 'outline', 'fill'] as const).map((key) => (
                    <ColourControl
                      id={`inset-${key}-colour`}
                      key={key}
                      label={`${key.charAt(0).toUpperCase()}${key.slice(1)} colour`}
                      value={
                        key === 'border'
                          ? composition.insetMap!.border.colour
                          : composition.insetMap!.indicator[
                              key === 'outline' ? 'outlineColour' : 'fillColour'
                            ]
                      }
                      onChange={(colour) => {
                        const inset = composition.insetMap!;
                        updateInset(
                          key === 'border'
                            ? { ...inset, border: { ...inset.border, colour } }
                            : {
                                ...inset,
                                indicator: {
                                  ...inset.indicator,
                                  [key === 'outline' ? 'outlineColour' : 'fillColour']: colour,
                                },
                              },
                        );
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    className="composition-secondary-action"
                    ref={insetActionRef}
                    onClick={() => {
                      updateInset(null);
                      requestAnimationFrame(() => insetActionRef.current?.focus());
                    }}
                  >
                    Remove inset map
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="composition-secondary-action"
                  onClick={() => updateInset(createDefaultInsetMap({ layers, annotationLayers }))}
                  ref={insetActionRef}
                >
                  Add inset map
                </button>
              )
            ) : null}
            {id === 'logo' ? (
              <>
                <input
                  accept={COMPOSITION_LOGO_ACCEPT}
                  aria-label="Choose logo image"
                  className="visually-hidden"
                  onChange={(event) => {
                    const input = event.currentTarget;
                    const file = input.files?.[0];
                    if (!file) return;
                    void importLogo(file).finally(() => {
                      input.value = '';
                    });
                  }}
                  ref={logoInputRef}
                  type="file"
                />
                {composition.logo ? (
                  <>
                    <p className="composition-logo-filename">
                      {compositionLogoDisplayName(composition.logo)}
                    </p>
                    <div className="composition-dimension-grid">
                      <label className="composition-field" htmlFor="composition-logo-width">
                        <span>Width (mm)</span>
                        <NumericDraftInput
                          id="composition-logo-width"
                          max={
                            compositionLogoWidthRangeMm(
                              composition.logo.bounds,
                              composition.logo.aspectRatio,
                              dimensions,
                            ).maximum
                          }
                          min={
                            compositionLogoWidthRangeMm(
                              composition.logo.bounds,
                              composition.logo.aspectRatio,
                              dimensions,
                            ).minimum
                          }
                          onCommit={(widthMm) => {
                            const bounds = compositionLogoBoundsForWidthMm(
                              composition.logo!.bounds,
                              composition.logo!.aspectRatio,
                              dimensions,
                              widthMm,
                            );
                            if (!bounds) {
                              setLogoError('Enter a valid logo width greater than zero.');
                              return;
                            }
                            const result = updateLogo({ ...composition.logo!, bounds });
                            setLogoError(result.ok ? null : result.message);
                          }}
                          step={0.1}
                          value={composition.logo.bounds.width * dimensions.width}
                        />
                      </label>
                      <label className="composition-field" htmlFor="composition-logo-height">
                        <span>Height (mm)</span>
                        <NumericDraftInput
                          id="composition-logo-height"
                          max={dimensions.height}
                          min={0}
                          onCommit={() => undefined}
                          readOnly
                          step={0.1}
                          value={composition.logo.bounds.height * dimensions.height}
                        />
                      </label>
                    </div>
                    <button
                      className="composition-secondary-action composition-icon-action"
                      onClick={() => {
                        updateLogo(null);
                        setLogoError(null);
                        requestAnimationFrame(() => logoActionRef.current?.focus());
                      }}
                      ref={logoActionRef}
                      type="button"
                    >
                      <Icon name="trash" /> Remove logo
                    </button>
                  </>
                ) : (
                  <button
                    aria-describedby="composition-logo-limits"
                    className="composition-secondary-action composition-icon-action"
                    disabled={logoImporting}
                    onClick={() => logoInputRef.current?.click()}
                    ref={logoActionRef}
                    type="button"
                  >
                    <Icon name="upload" /> {logoImporting ? 'Reading imageâ€¦' : 'Add logo'}
                  </button>
                )}
                <small className="composition-clipping-note" id="composition-logo-limits">
                  PNG or JPEG, up to 5 MiB and 8,192 px per dimension. Drag or resize the logo on
                  the page; the image keeps its proportions inside the frame.
                </small>
                {logoError ? (
                  <p className="import-error composition-logo-error" role="alert">
                    {logoError}
                  </p>
                ) : null}
              </>
            ) : null}

            {id === 'sources-attribution' ? (
              <>
                <p className="composition-source-preview">
                  {mandatoryAttribution ?? 'No mandatory basemap attribution for the current map.'}
                </p>
                <small className="composition-clipping-note">
                  Required source credit is generated from visible built-in sources and cannot be
                  removed. Drag it to position it; use corner or side handles to change its wrapping
                  width.
                </small>
                <label className="composition-field" htmlFor="composition-attribution-width">
                  <span>Width (% of page)</span>
                  <NumericDraftInput
                    id="composition-attribution-width"
                    min={2}
                    max={(1 - composition.sourcesAttribution.position.x) * 100}
                    step={1}
                    value={
                      (composition.sourcesAttribution.width ??
                        1 - composition.sourcesAttribution.position.x) * 100
                    }
                    onCommit={(width) => updateSourcesAttribution({ width: width / 100 })}
                  />
                </label>
                <TypographyControls
                  id="composition-sources-attribution"
                  maximumFontSizePt={COMPOSITION_SOURCES_ATTRIBUTION_MAX_PT}
                  minimumFontSizePt={COMPOSITION_SOURCES_ATTRIBUTION_MIN_PT}
                  onChange={(typography) => updateSourcesAttribution({ typography })}
                  typography={composition.sourcesAttribution.typography}
                />
              </>
            ) : null}

            {id === 'guides' ? (
              <>
                <label className="composition-field" htmlFor="composition-grid-size">
                  <span>Grid size (mm)</span>
                  <NumericDraftInput
                    id="composition-grid-size"
                    max={COMPOSITION_GRID_SPACING_MAX_MM}
                    min={COMPOSITION_GRID_SPACING_MIN_MM}
                    onCommit={setGridSpacingMm}
                    step={1}
                    value={gridSpacingMm}
                  />
                </label>
                <label className="composition-check">
                  <input
                    checked={gridVisible}
                    onChange={(event) => setGridVisible(event.target.checked)}
                    type="checkbox"
                  />
                  <span>Show grid</span>
                </label>
                <label className="composition-check">
                  <input
                    checked={snapEnabled}
                    onChange={(event) => setSnapEnabled(event.target.checked)}
                    type="checkbox"
                  />
                  <span>Snap to grid</span>
                </label>
                <label className="composition-check">
                  <input
                    checked={objectSnapEnabled}
                    onChange={(event) => setObjectSnapEnabled(event.target.checked)}
                    type="checkbox"
                  />
                  <span>Snap to objects</span>
                </label>
                <small className="composition-page-dimensions">
                  {gridSpacingMm} mm grid, page edges, and page centres.
                </small>
              </>
            ) : null}
          </AccordionSection>
        ))}
      </div>
      {exportOpen ? <ExportMapDialog onClose={closeExport} /> : null}
    </aside>
  );
}
